import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_e55312d9-2654-4e67-82f7-261766d13168/artifacts/8b215ca487d0b10f_IMG-20260910-WA0015.jpg";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/research-use", label: "Research Use" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b-[3px] border-black">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO}
            alt="UK Peptide Research"
            className="w-11 h-11 md:w-12 md:h-12 object-cover border-[3px] border-black shadow-brut-sm"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-base md:text-lg">UK Peptide</div>
            <div className="font-display text-[#ff1493] text-base md:text-lg -mt-1">Research</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-mono-ui text-xs font-bold px-4 py-2 border-[3px] border-transparent hover:border-black hover:bg-[#00d4ff] transition-colors ${
                  isActive ? "border-black bg-[#ff1493] text-white" : "text-black"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative inline-flex items-center gap-2 px-3 py-2 border-[3px] border-black bg-white shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
          >
            <ShoppingCart className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">CART</span>
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-[#ff1493] text-white border-[2px] border-black text-[10px] font-bold w-6 h-6 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className="inline-flex items-center gap-2 px-3 py-2 border-[3px] border-black bg-[#00d4ff] shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
                >
                  <LayoutDashboard className="w-4 h-4" strokeWidth={2.5} /> ADMIN
                </Link>
              )}
              <Link
                to="/account"
                data-testid="nav-account"
                className="inline-flex items-center gap-2 px-3 py-2 border-[3px] border-black bg-white shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
              >
                <User className="w-4 h-4" strokeWidth={2.5} /> {user.name.split(" ")[0]}
              </Link>
              <button
                onClick={onLogout}
                data-testid="nav-logout"
                className="inline-flex items-center gap-2 px-3 py-2 border-[3px] border-black bg-black text-white shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
              >
                <LogOut className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 border-[3px] border-black bg-[#ff1493] text-white shadow-brut-sm btn-press font-mono-ui text-xs font-bold"
            >
              SIGN IN
            </Link>
          )}

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 border-[3px] border-black shadow-brut-sm btn-press"
            aria-label="toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-[3px] border-black bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-white"
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-[#00d4ff]">
                    ADMIN
                  </Link>
                )}
                <Link to="/account" onClick={() => setOpen(false)} className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-white">
                  MY ACCOUNT
                </Link>
                <button onClick={() => { setOpen(false); onLogout(); }} className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-black text-white text-left">
                  SIGN OUT
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-[#ff1493] text-white">
                  SIGN IN
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="font-mono-ui text-sm font-bold px-4 py-3 border-[3px] border-black bg-white">
                  CREATE ACCOUNT
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
